import { trackEvent, SegmentEvents } from '@nile/mantine-theme/analytics'
import { extractErrorDetails } from './extractErrorDetails'
import { redactCarePartnerLinkTokens } from './analytics/carePartnerTelemetryScrub'
import { redactDocusignUrls } from './analytics/docusignTelemetryScrub'

/** Strip DocuSign signing URLs and care-partner link tokens before Segment client-error events leave the browser. */
function redactTelemetryText(text: string): string {
    return redactCarePartnerLinkTokens(redactDocusignUrls(text))
}

export type ClientErrorSource =
    | 'global_error_boundary'
    | 'route_error_boundary'
    | 'window_onerror'
    | 'unhandled_rejection'

interface TrackClientErrorParams {
    error: unknown
    source: ClientErrorSource
    componentStack?: string | null
}

export function trackClientError({ error, source, componentStack }: TrackClientErrorParams) {
    try {
        const details = extractErrorDetails(error, { maxStackLength: 512 })

        // ResizeObserver noise (browser internals, not actionable) — same filter as Datadog RUM.
        if (details.message.indexOf('ResizeObserver loop') !== -1) {
            return
        }

        trackEvent(SegmentEvents.CLIENT_ERROR, {
            error_name: details.name,
            error_message: redactTelemetryText(details.message).substring(0, 500),
            error_stack: details.stack && redactTelemetryText(details.stack),
            source,
            page:
                typeof window !== 'undefined'
                    ? redactCarePartnerLinkTokens(window.location.pathname)
                    : 'unknown',
            url:
                typeof window !== 'undefined'
                    ? redactCarePartnerLinkTokens(window.location.href)
                    : 'unknown',
            ...(componentStack && { component_stack: componentStack.substring(0, 512) }),
        })
    } catch {
        // Never let error tracking itself cause user-facing errors
    }
}
